import { FC, useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { Textbox } from "capitalrx-components-2.0/lib/forms";
import { ProviderPaymentRemittanceDataForm } from "./PaymentRemittanceGroupFields";

export const formatValueBankAccountNumber = (val: string) => {
  const number = (val ?? "").replace(/[^0-9,*]/g, "");
  return number;
};
export const formatValueBankAccountNumberSecret = (val: string) => {
  const number = formatValueBankAccountNumber(val);
  return number.replace(/./g, "*").slice(0, -4) + number.slice(-4);
};

const nameField = "bank_account_number";

export interface BankAccountNumberFieldProps {
  readonly readOnly: boolean;
}

export const BankAccountNumberField: FC<BankAccountNumberFieldProps> = ({ readOnly }) => {
  if (readOnly) {
    return <BankAccountNumberReadOnly />;
  }

  return <BankAccountNumber />;
};

export const BankAccountNumberReadOnly = () => {
  const { control } = useFormContext<ProviderPaymentRemittanceDataForm>();

  const bankAccountNumber = useWatch({
    control,
    name: nameField,
  });

  return (
    <Textbox
      key={`${nameField}_secret`}
      label="Bank Account Number"
      testId={`${nameField}_secret`}
      instructions="Include all zeroes"
      value={formatValueBankAccountNumberSecret(bankAccountNumber)}
      readOnly
    />
  );
};

export const BankAccountNumber = () => {
  const { setValue, watch, setError, clearErrors } = useFormContext<ProviderPaymentRemittanceDataForm>();

  useEffect(() => {
    const { unsubscribe } = watch((value, { name }) => {
      if (name !== nameField) return;

      const masked = formatValueBankAccountNumber(value[nameField] ?? "");
      if (value[nameField] !== masked) {
        setValue(nameField, masked, { shouldValidate: false });
      }

      // Validate length: 8–17 digits inclusive
      const len = masked.length;
      if (len === 0) {
        setError(nameField, {
          type: "required",
          message: "Bank account number is required",
        });
      } else if (len < 8 || len > 17) {
        setError(nameField, {
          type: "minMax",
          message: "Bank account number must be between 8 and 17 digits",
        });
      } else {
        clearErrors(nameField);
      }
    });

    return () => unsubscribe();
  }, [setValue, watch, setError, clearErrors]);

  return (
    <Textbox.Field
      label="Bank Account Number"
      key={nameField}
      name={nameField}
      testId={nameField}
      instructions="Include all zeroes"
      required
    />
  );
};
